import { Link, matchPath, useLocation } from "react-router";
import {
  LayoutDashboard,
  Briefcase,
  FileText,
  Receipt,
  Users,
  Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useConfigurationContext } from "../root/ConfigurationContext";

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
}

const NavItem = ({ to, icon, label, isActive }: NavItemProps) => (
  <Link
    to={to}
    className={cn(
      "flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all duration-200 rounded-lg relative group",
      isActive
        ? "text-primary bg-primary/10 shadow-sm"
        : "text-muted-foreground hover:text-foreground hover:bg-muted/80",
    )}
  >
    <span
      className={cn(
        "w-5 h-5 transition-transform duration-200 group-hover:scale-110",
        isActive && "text-primary",
      )}
    >
      {icon}
    </span>
    <span className="transition-colors duration-200">{label}</span>
    {isActive && (
      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-r-full" />
    )}
  </Link>
);

export const Sidebar = () => {
  const location = useLocation();
  const config = useConfigurationContext();

  const getCurrentPath = () => {
    if (matchPath("/", location.pathname)) return "/";
    if (matchPath("/contacts/*", location.pathname)) return "/contacts";
    if (matchPath("/jobs/*", location.pathname)) return "/jobs";
    if (matchPath("/proposals/*", location.pathname)) return "/proposals";
    if (matchPath("/invoices/*", location.pathname)) return "/invoices";
    if (matchPath("/settings/*", location.pathname)) return "/settings";
    return "";
  };

  const currentPath = getCurrentPath();

  // Use favicon as sidebar logo
  const logoUrl = config.favicon;
  const companyName = config.companyName || config.title || "Finturf";

  return (
    <aside className="hidden lg:flex flex-col w-52 h-screen bg-background border-r border-border shrink-0">
      {/* Logo */}
      <Link
        to="/"
        className="flex items-center gap-2 px-4 h-16 border-b border-border hover:bg-muted/50 transition-colors"
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={companyName}
            className="w-8 h-8 rounded object-contain"
          />
        ) : (
          <div className="w-8 h-8 rounded bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-semibold text-sm">
              {companyName.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <span className="text-lg font-semibold truncate">{companyName}</span>
      </Link>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        <NavItem
          to="/"
          icon={<LayoutDashboard className="w-5 h-5" />}
          label="Dashboard"
          isActive={currentPath === "/"}
        />
        <NavItem
          to="/jobs"
          icon={<Briefcase className="w-5 h-5" />}
          label="Jobs"
          isActive={currentPath === "/jobs"}
        />
        <NavItem
          to="/contacts"
          icon={<Users className="w-5 h-5" />}
          label="Contacts"
          isActive={currentPath === "/contacts"}
        />
        <NavItem
          to="/proposals"
          icon={<FileText className="w-5 h-5" />}
          label="Proposals"
          isActive={currentPath === "/proposals"}
        />
        <NavItem
          to="/invoices"
          icon={<Receipt className="w-5 h-5" />}
          label="Invoices"
          isActive={currentPath === "/invoices"}
        />
      </nav>

      {/* Bottom Settings */}
      <div className="p-3 border-t border-border">
        <NavItem
          to="/settings"
          icon={<Settings className="w-5 h-5" />}
          label="Settings"
          isActive={currentPath === "/settings"}
        />
      </div>
    </aside>
  );
};
